import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { studiesApi, catalogApi } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Plus, Pencil, Trash2, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Studies() {
  const [studies, setStudies] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [containers, setContainers] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    area_id: "",
    sample_type_id: "",
    container_id: "",
    method_id: "",
    active: true,
    barcode_suffix: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studiesRes, areasRes, sampleTypesRes, containersRes, methodsRes] =
        await Promise.all([
          studiesApi.getAll(),
          catalogApi.areas.getAll(),
          catalogApi.sampleTypes.getAll(),
          catalogApi.containers.getAll(),
          catalogApi.methods.getAll(),
        ]);
      setStudies(studiesRes.data);
      setAreas(areasRes.data);
      setSampleTypes(sampleTypesRes.data);
      setContainers(containersRes.data);
      setMethods(methodsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (study = null) => {
    if (study) {
      setEditingStudy(study);
      setFormData({
        name: study.name,
        code: study.code,
        description: study.description || "",
        area_id: study.area_id || "",
        sample_type_id: study.sample_type_id || "",
        container_id: study.container_id || "",
        method_id: study.method_id || "",
        active: study.active,
        barcode_suffix: (study.barcode_suffix || "").replace("-", "."),
      });
    } else {
      setEditingStudy(null);
      setFormData({
        name: "",
        code: "",
        description: "",
        area_id: "",
        sample_type_id: "",
        container_id: "",
        method_id: "",
        active: true,
        barcode_suffix: "",
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.area_id) dataToSend.area_id = null;
      if (!dataToSend.sample_type_id) dataToSend.sample_type_id = null;
      if (!dataToSend.container_id) dataToSend.container_id = null;
      if (!dataToSend.method_id) dataToSend.method_id = null;
      if (!dataToSend.barcode_suffix) dataToSend.barcode_suffix = null;

      if (editingStudy) {
        await studiesApi.update(editingStudy.id, dataToSend);
        toast.success("Estudio actualizado");
      } else {
        await studiesApi.create(dataToSend);
        toast.success("Estudio creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar estudio");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar este estudio?")) return;
    try {
      await studiesApi.delete(id);
      toast.success("Estudio eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const filteredStudies = studies.filter(
    (study) =>
      study.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      study.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAreaName = (id) => areas.find((a) => a.id === id)?.name || "-";
  const getContainerName = (id) => containers.find((c) => c.id === id)?.name || "-";
  const getSampleTypeName = (id) => sampleTypes.find((s) => s.id === id)?.name || "-";

  return (
    <div className="space-y-6" data-testid="studies-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Estudios
          </h1>
          <p className="text-muted-foreground">
            Administre el catálogo de estudios (los precios se asignan en Listas de Precios)
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-study-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Estudio
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FlaskConical className="h-5 w-5" />
              Catálogo de Estudios
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-studies"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Recipiente</TableHead>
                  <TableHead>Tipo Muestra</TableHead>
                  <TableHead>Sufijo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudies.map((study) => (
                  <TableRow key={study.id}>
                    <TableCell className="font-mono font-bold">{study.code}</TableCell>
                    <TableCell className="font-medium">{study.name}</TableCell>
                    <TableCell>{getAreaName(study.area_id)}</TableCell>
                    <TableCell>{getContainerName(study.container_id)}</TableCell>
                    <TableCell>{getSampleTypeName(study.sample_type_id)}</TableCell>
                    <TableCell>
                      {study.barcode_suffix ? (
                        <Badge variant="outline" className="font-mono">{study.barcode_suffix}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={study.active ? "default" : "secondary"}>
                        {study.active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(study)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(study.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingStudy ? "Editar Estudio" : "Nuevo Estudio"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  placeholder="BH, QS6, EGO"
                  required
                  data-testid="study-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Biometría Hemática"
                  required
                  data-testid="study-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Área</Label>
                <Select
                  value={formData.area_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, area_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area.id} value={area.id}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Muestra</Label>
                <Select
                  value={formData.sample_type_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, sample_type_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {sampleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Recipiente</Label>
                <Select
                  value={formData.container_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, container_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {containers.map((container) => (
                      <SelectItem key={container.id} value={container.id}>
                        {container.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Método</Label>
                <Select
                  value={formData.method_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, method_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {methods.map((method) => (
                      <SelectItem key={method.id} value={method.id}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sufijo cód. barras</Label>
                <Select
                  value={formData.barcode_suffix || "none"}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, barcode_suffix: value === "none" ? "" : value }))
                  }
                >
                  <SelectTrigger data-testid="study-barcode-suffix">
                    <SelectValue placeholder="Sin sufijo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin sufijo</SelectItem>
                    <SelectItem value=".1">.1</SelectItem>
                    <SelectItem value=".2">.2</SelectItem>
                    <SelectItem value=".3">.3</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Se agrega al folio en la etiqueta (ej. 260810002.1) para que el analizador acepte el tubo aunque el folio se repita. El LIS lo quita y guarda en el folio real.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <Label>Estado del estudio</Label>
              <div className="flex items-center gap-2">
                <span className={`text-sm ${formData.active ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                  {formData.active ? "Activo" : "Inactivo"}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.active}
                  onClick={() => setFormData((prev) => ({ ...prev, active: !prev.active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.active ? 'bg-green-600' : 'bg-gray-300'}`}
                  data-testid="study-active-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-study-btn">
                {editingStudy ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
